import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@client';
import { FinancialPaymentResponse } from '@module/financial/model/FinancialPaymentResponse';
import { FinancialPaymentRefundResponse } from '@module/financial/model/FinancialPaymentRefundResponse';
import { MatchablePaymentResponse } from '@module/financial/model/MatchablePaymentResponse';
import { financialPaymentsApi } from '../financial-payments';

export const financialRefundsApi = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ['BuyerUnmatchedRefunds'],
  reducerPath: 'financial-refunds_api',
  endpoints: (build) => ({
    fetchPendingRefunds: build.query<
      FinancialPaymentRefundResponse[],
      { paymentId: number }
    >({
      query: ({ paymentId }) => ({
        url: `/secured/financial/payments/${paymentId}/pending-refunds`,
        method: 'GET'
      })
    }),
    fetchBuyerUnmatchedRefunds: build.query<
      FinancialPaymentRefundResponse[],
      { buyerId: number }
    >({
      query: ({ buyerId }) => ({
        url: `/secured/financial/buyers/${buyerId}/unmatched-refunds`,
        method: 'GET'
      }),
      providesTags: ['BuyerUnmatchedRefunds']
    }),
    fetchMatchablePayments: build.query<
      MatchablePaymentResponse[],
      { refundId: number }
    >({
      query: ({ refundId }) => ({
        url: `/secured/financial/refunds/${refundId}/matchable-payments`,
        method: 'GET'
      })
    }),
    refundPayment: build.mutation<
      FinancialPaymentResponse,
      { paymentId: number; amount: number }
    >({
      query: ({ paymentId, amount }) => ({
        url: `/secured/financial/payments/${paymentId}/refund`,
        method: 'POST',
        data: { amount }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: financialPayment } = await queryFulfilled;
          dispatch(
            financialPaymentsApi.util.updateQueryData(
              'fetchPayment',
              { paymentId: financialPayment.id },
              (draft) => {
                Object.assign(draft, financialPayment);
              }
            )
          );
        } catch {
          //
        }
      }
    }),
    matchRefund: build.mutation<
      FinancialPaymentResponse,
      { paymentId: number; refundId: number }
    >({
      query: ({ paymentId, refundId }) => ({
        url: `/secured/financial/payments/${paymentId}/refunds/${refundId}/match`,
        method: 'POST'
      }),
      invalidatesTags: ['BuyerUnmatchedRefunds'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: financialPayment } = await queryFulfilled;
          dispatch(
            financialPaymentsApi.util.updateQueryData(
              'fetchPayment',
              { paymentId: financialPayment.id },
              (draft) => {
                Object.assign(draft, financialPayment);
              }
            )
          );
        } catch {
          //
        }
      }
    }),
    deleteRefund: build.mutation<void, { refundId: number }>({
      query: ({ refundId }) => ({
        url: `/secured/financial/refunds/${refundId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['BuyerUnmatchedRefunds']
    })
  })
});

export const {
  useFetchPendingRefundsQuery,
  useFetchBuyerUnmatchedRefundsQuery,
  useFetchMatchablePaymentsQuery,
  useRefundPaymentMutation,
  useMatchRefundMutation,
  useDeleteRefundMutation
} = financialRefundsApi;
